/**
 * LightingPanel — floating slider overlay to tweak scene lighting
 * intensities while we settle on the final look.
 *
 * Hidden by default on prod. To re-enable for emergency tuning, open
 * the site with `?lighting` in the URL (e.g.
 * https://www.sachersantana.com/?lighting). Dev builds always show it.
 */

import { useState } from 'react'
import { useLighting } from '@/context/LightingContext'
import './LightingPanel.css'

function shouldShowLightingPanel(): boolean {
  if (import.meta.env.DEV) return true
  if (typeof window === 'undefined') return false
  return window.location.search.includes('lighting')
}

const SLIDERS: { key: keyof ReturnType<typeof useLighting>['settings']; label: string; min: number; max: number; step: number }[] = [
  { key: 'ambient',        label: 'Ambient',         min: 0, max: 5,    step: 0.05 },
  { key: 'key',            label: 'Key Light',       min: 0, max: 10,   step: 0.1  },
  { key: 'fill',           label: 'Fill Light',      min: 0, max: 5,    step: 0.05 },
  { key: 'rim',            label: 'Rim Light',       min: 0, max: 5,    step: 0.05 },
  { key: 'hemisphere',     label: 'Hemisphere',      min: 0, max: 5,    step: 0.05 },
  { key: 'backgroundHalo', label: 'Background Halo', min: 0, max: 1,    step: 0.01 },
  { key: 'bgLightness',    label: 'BG Lightness',    min: 0, max: 40,   step: 0.5  },
]

export function LightingPanel() {
  const [open, setOpen] = useState(false)
  const { settings, setSetting, reset } = useLighting()

  if (!shouldShowLightingPanel()) return null

  return (
    <>
      <button
        className="lighting-toggle"
        onClick={() => setOpen(o => !o)}
        aria-label="Lighting settings"
        title="Lighting (toggle)"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 18h6" />
          <path d="M10 22h4" />
          <path d="M12 2a7 7 0 0 0-4 12.7c1 .8 1.6 2 1.7 3.3h4.6c.1-1.3.7-2.5 1.7-3.3A7 7 0 0 0 12 2Z" />
        </svg>
      </button>

      {open && (
        <div className="lighting-panel">
          <div className="lighting-panel-header">
            <h3>Lighting</h3>
            <button className="lighting-close" onClick={() => setOpen(false)} aria-label="Close">×</button>
          </div>

          <div className="lighting-controls">
            {SLIDERS.map(s => (
              <label key={s.key} className="lighting-row">
                <span className="lighting-label">{s.label}</span>
                <input
                  type="range"
                  min={s.min}
                  max={s.max}
                  step={s.step}
                  value={settings[s.key]}
                  onChange={e => setSetting(s.key, parseFloat(e.target.value))}
                />
                <span className="lighting-value">{settings[s.key].toFixed(2)}</span>
              </label>
            ))}
          </div>

          <div className="lighting-footer">
            <button className="lighting-reset" onClick={reset}>Reset</button>
            <code className="lighting-snippet">{JSON.stringify(settings)}</code>
          </div>
        </div>
      )}
    </>
  )
}
